import React, { useEffect, useState } from 'react';
import {
  Button,
  HStack,
  Highlight,
  IconBannersWarning,
  LoadingSpinner,
  RadioListItem,
  ScrollView,
  Text,
  VStack,
} from '@adc/parallax-component-library';
import { Dialog } from 'tamagui';
import i18n from '../../utilities/i18n';
import { useAppSelector } from '../../reducers/hooks';
import { store } from '../../reducers';
import { changeCurrentOrganization } from '../../reducers/organization/reducer';
import { useQuery } from '@tanstack/react-query';
import { organizationsQueryOptions } from '../../queryOptions';
import { setSessionObject } from '../../utilities/storage';
import { useLocation, useNavigate } from 'react-router';
import { ROUTES } from '../../constants';
import './Modal.scss';

type Props = {
  open: boolean;
  onClose: () => void;
};

export function ChangeOrganizationModal({ open, onClose }: Props) {
  const organizations = useQuery(organizationsQueryOptions());
  const location = useLocation();
  const navigate = useNavigate();

  const { currentOrgKey } = useAppSelector((state) => state.organizations);

  const [newCurrentOrganization, setNewCurrentOrganization] = useState('');

  useEffect(() => {
    setNewCurrentOrganization(currentOrgKey);
  }, [currentOrgKey]);

  const handleOrganizationChange = () => {
    store.dispatch(changeCurrentOrganization(newCurrentOrganization));
    setSessionObject('currentOrganization', newCurrentOrganization);

    if (shouldRedirectOnChange) {
      navigate(ROUTES.STUDIES.ROOT);
    }

    onClose();
  };

  const shouldRedirectOnChange =
    location.pathname.startsWith(ROUTES.STUDIES.ROOT) ||
    location.pathname.startsWith(ROUTES.SITES.ROOT) ||
    location.pathname.startsWith(ROUTES.SUBJECTS.ROOT) ||
    location.pathname.startsWith(ROUTES.SENSORS.ROOT);

  return (
    <Dialog open={open}>
      <Dialog.Portal>
        <Dialog.Overlay key="overlay" />
        <Dialog.Content key="content">
          <VStack gap="$2">
            <Dialog.Title>
              <Text type="$h3.strong">
                {i18n.t('Modal_ChangeOrganization.title')}
              </Text>
            </Dialog.Title>
          </VStack>

          <ScrollView flex={1} minHeight={0} marginVertical="$4">
            <VStack gap="$2">
              {organizations.isPending ? (
                <VStack
                  alignItems="center"
                  justifyContent="center"
                  flexGrow={1}
                >
                  <LoadingSpinner testID="change-organizations-loading" />
                </VStack>
              ) : null}

              {organizations.isError ? (
                <Highlight
                  variant="danger"
                  iconStart={
                    <IconBannersWarning
                      color="$neutral.0"
                      accentColor="$support.danger.default"
                    />
                  }
                  title={i18n.t('Modal_ChangeOrganization.error_title')}
                  body={i18n.t('Modal_ChangeOrganization.error_generic')}
                  testID="change-organizations-error-banner"
                />
              ) : null}

              {organizations.isSuccess
                ? organizations.data.map(({ key, name, description }) => (
                    <RadioListItem
                      key={key}
                      title={name}
                      selected={newCurrentOrganization === key}
                      onPress={() => setNewCurrentOrganization(key)}
                      disabled={false}
                      label={description}
                      testID={`organization-${key}`}
                    />
                  ))
                : null}
            </VStack>
          </ScrollView>

          <HStack justifyContent="flex-end" gap="$2" flexShrink={0}>
            <Button
              variant="secondary"
              label={i18n.t('Common.cancel')}
              onPress={onClose}
              testID="change-organization-cancel-button"
            />
            <Button
              variant="primary"
              label={i18n.t('Common.select')}
              onPress={handleOrganizationChange}
              testID="change-organization-select-button"
              disabled={newCurrentOrganization === currentOrgKey}
            />
          </HStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
